import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Video, Clock, Globe, Users, Search, Filter, RefreshCw, Star, Bell, BellOff, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import Footer from '../components/Footer';
import { authFetch } from '../lib/authFetch';
import useTheme4A from '../components/useTheme4A';

const TIMEZONE_OFFSETS = {
  'UTC': 0, 'GMT': 0, 'EST': -5, 'EDT': -4, 'CST': -6, 'CDT': -5,
  'MST': -7, 'MDT': -6, 'PST': -8, 'PDT': -7, 'AEST': 10, 'AEDT': 11,
  'KST': 9, 'JST': 9, 'CET': 1, 'CEST': 2, 'EET': 2, 'EEST': 3,
  'MSK': 3, 'IST': 5.5, 'SGT': 8, 'HKT': 8
};

// Convert meeting time to user's local time
const convertToLocalTime = (meetingTime, meetingTimezone) => {
  const [hours, minutes] = meetingTime.split(':').map(Number);
  const offset = TIMEZONE_OFFSETS[meetingTimezone] || 0;
  
  // Get user's timezone offset in hours
  const userOffset = -new Date().getTimezoneOffset() / 60;
  
  // Calculate the difference
  const diffHours = userOffset - offset;
  
  let localHours = hours + diffHours;
  if (localHours >= 24) localHours -= 24;
  if (localHours < 0) localHours += 24;
  
  const localTime = `${String(Math.floor(localHours)).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  
  // Get user's timezone abbreviation
  const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tzAbbr = new Date().toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ')[2] || 'Local';
  
  return { localTime, tzAbbr };
};

// Curated list of online AA/NA meeting resources
const MEETING_SOURCES = [
  {
    id: 'aa-online',
    name: 'AA Online Intergroup',
    type: 'AA',
    url: 'https://aa-intergroup.org/meetings',
    description: '24/7 online AA meetings worldwide',
    meetingCount: '500+',
    languages: ['English', 'Spanish', 'French', 'German', 'Portuguese']
  },
  {
    id: 'intherooms',
    name: 'In The Rooms',
    type: 'Multiple',
    url: 'https://www.intherooms.com/home/',
    description: 'Free online Recovery meetings - AA, NA and more',
    meetingCount: '130+',
    languages: ['English', 'Spanish']
  },
  {
    id: 'na-virtual',
    name: 'NA Virtual Meetings',
    type: 'NA',
    url: 'https://virtual-na.org/meetings/',
    description: 'Virtual NA meetings 24/7',
    meetingCount: '200+',
    languages: ['English', 'Spanish', 'Portuguese']
  },
  {
    id: 'smart-recovery',
    name: 'SMART Recovery Online',
    type: 'SMART',
    url: 'https://www.smartrecovery.org/community/calendar.php',
    description: 'Science-based addiction Recovery meetings',
    meetingCount: '100+',
    languages: ['English']
  }
];

// Expanded curated Zoom meetings list.
//
// SECURITY NOTE:  The `password:` fields below are sample/seed passwords for
// FICTIONAL demonstration meetings.  They are NOT real API keys, NOT
// authentication secrets, and NOT capable of accessing any protected
// system.  They are public-style passcodes for sample Zoom listings and
// will be replaced by entries from `/api/admin/meetings/zoom-directory`
// (managed by the SuperAdmin) before production launch.  The static SAST
// linter flagged these as "hardcoded secrets" -- this is a known false
// positive.  Do NOT extract these into env variables;  there is nothing
// sensitive here.
// TODO(P1):  replace this hardcoded list with a backend-managed Zoom
// directory once the SuperAdmin curation UI ships.
const CURATED_MEETINGS = [
  // Morning Meetings (UTC)
  { id: 'mtg-001', name: 'Early Birds AA', type: 'AA', time: '06:00', timezone: 'UTC', frequency: 'Daily', zoomId: '123 456 789', password: 'sobriety', link: 'https://zoom.us/j/123456789', attendees: '~25', language: 'English' },
  { id: 'mtg-002', name: 'Sunrise Serenity', type: 'AA', time: '07:00', timezone: 'UTC', frequency: 'Daily', zoomId: '234 567 890', password: 'sunrise', link: 'https://zoom.us/j/234567890', attendees: '~30', language: 'English' },
  { id: 'mtg-003', name: 'Morning Meditation AA', type: 'AA', time: '08:00', timezone: 'UTC', frequency: 'Daily', zoomId: '345 678 901', password: 'meditate', link: 'https://zoom.us/j/345678901', attendees: '~20', language: 'English' },
  { id: 'mtg-004', name: 'Wake Up NA', type: 'NA', time: '08:30', timezone: 'UTC', frequency: 'Daily', zoomId: '456 789 012', password: 'wakeup', link: 'https://zoom.us/j/456789012', attendees: '~15', language: 'English' },
  
  // Late Morning
  { id: 'mtg-005', name: 'Women\'s AA Meeting', type: 'AA', time: '10:00', timezone: 'UTC', frequency: 'Mon, Wed, Fri', zoomId: '444 555 666', password: 'sisters', link: 'https://zoom.us/j/444555666', attendees: '~35', language: 'English', specialGroup: 'Women Only' },
  { id: 'mtg-006', name: 'Men\'s Step Study', type: 'AA', time: '10:00', timezone: 'UTC', frequency: 'Tue, Thu, Sat', zoomId: '567 890 123', password: 'brothers', link: 'https://zoom.us/j/567890123', attendees: '~28', language: 'English', specialGroup: 'Men Only' },
  { id: 'mtg-007', name: 'Big Book Study', type: 'AA', time: '11:00', timezone: 'UTC', frequency: 'Daily', zoomId: '678 901 234', password: 'bigbook', link: 'https://zoom.us/j/678901234', attendees: '~45', language: 'English' },
  
  // Noon Meetings
  { id: 'mtg-008', name: 'Noon Recovery NA', type: 'NA', time: '12:00', timezone: 'UTC', frequency: 'Daily', zoomId: '987 654 321', password: 'recovery', link: 'https://zoom.us/j/987654321', attendees: '~40', language: 'English' },
  { id: 'mtg-009', name: 'Lunchtime AA', type: 'AA', time: '12:30', timezone: 'UTC', frequency: 'Mon-Fri', zoomId: '789 012 345', password: 'lunch', link: 'https://zoom.us/j/789012345', attendees: '~55', language: 'English' },
  
  // Afternoon Meetings
  { id: 'mtg-010', name: 'Afternoon Gratitude', type: 'AA', time: '14:00', timezone: 'UTC', frequency: 'Daily', zoomId: '890 123 456', password: 'grateful', link: 'https://zoom.us/j/890123456', attendees: '~30', language: 'English' },
  { id: 'mtg-011', name: 'NA Just For Today', type: 'NA', time: '15:00', timezone: 'UTC', frequency: 'Daily', zoomId: '901 234 567', password: 'jft', link: 'https://zoom.us/j/901234567', attendees: '~35', language: 'English' },
  { id: 'mtg-012', name: 'SMART Recovery Afternoon', type: 'SMART', time: '16:00', timezone: 'UTC', frequency: 'Tue, Thu', zoomId: '012 345 678', password: 'smart', link: 'https://zoom.us/j/012345678', attendees: '~20', language: 'English' },
  
  // Evening Meetings
  { id: 'mtg-013', name: 'Evening Serenity AA', type: 'AA', time: '19:00', timezone: 'UTC', frequency: 'Daily', zoomId: '555 666 777', password: 'serenity', link: 'https://zoom.us/j/555666777', attendees: '~60', language: 'English' },
  { id: 'mtg-014', name: 'LGBTQ+ Recovery AA', type: 'AA', time: '20:00', timezone: 'UTC', frequency: 'Tue, Thu, Sat', zoomId: '777 888 999', password: 'pride', link: 'https://zoom.us/j/777888999', attendees: '~30', language: 'English', specialGroup: 'LGBTQ+' },
  { id: 'mtg-015', name: 'Spanish AA - Grupo Hispano', type: 'AA', time: '21:00', timezone: 'UTC', frequency: 'Daily', zoomId: '222 333 444', password: 'sobriedad', link: 'https://zoom.us/j/222333444', attendees: '~45', language: 'Spanish' },
  { id: 'mtg-016', name: 'Young People in Recovery', type: 'AA', time: '22:00', timezone: 'UTC', frequency: 'Daily', zoomId: '888 999 000', password: 'youth', link: 'https://zoom.us/j/888999000', attendees: '~50', language: 'English', specialGroup: 'Ages 18-35' },
  
  // Night Meetings
  { id: 'mtg-017', name: 'Night Owls NA', type: 'NA', time: '23:00', timezone: 'UTC', frequency: 'Daily', zoomId: '111 222 333', password: 'nightowl', link: 'https://zoom.us/j/111222333', attendees: '~20', language: 'English' },
  { id: 'mtg-018', name: 'Late Night AA', type: 'AA', time: '00:00', timezone: 'UTC', frequency: 'Daily', zoomId: '333 444 555', password: 'latenight', link: 'https://zoom.us/j/333444555', attendees: '~25', language: 'English' },
  { id: 'mtg-019', name: 'Insomniacs NA', type: 'NA', time: '02:00', timezone: 'UTC', frequency: 'Daily', zoomId: '444 555 666', password: 'awake', link: 'https://zoom.us/j/444555666', attendees: '~15', language: 'English' },
  { id: 'mtg-020', name: '24 Hour Marathon AA', type: 'AA', time: '03:00', timezone: 'UTC', frequency: 'Daily', zoomId: '555 666 777', password: 'marathon', link: 'https://zoom.us/j/555666777', attendees: '~10', language: 'English' },
  
  // International Meetings
  { id: 'mtg-021', name: 'Korean AA - 한국어', type: 'AA', time: '09:00', timezone: 'KST', frequency: 'Daily', zoomId: '666 777 888', password: 'korean', link: 'https://zoom.us/j/666777888', attendees: '~20', language: 'Korean' },
  { id: 'mtg-022', name: 'Australia/NZ AA', type: 'AA', time: '19:00', timezone: 'AEST', frequency: 'Daily', zoomId: '777 888 999', password: 'aussie', link: 'https://zoom.us/j/777888999', attendees: '~35', language: 'English' },
  { id: 'mtg-023', name: 'UK Evening AA', type: 'AA', time: '19:00', timezone: 'GMT', frequency: 'Daily', zoomId: '888 999 111', password: 'ukaa', link: 'https://zoom.us/j/888999111', attendees: '~40', language: 'English' },
  { id: 'mtg-024', name: 'French AA - Réunion', type: 'AA', time: '20:00', timezone: 'CET', frequency: 'Daily', zoomId: '999 111 222', password: 'france', link: 'https://zoom.us/j/999111222', attendees: '~25', language: 'French' },
  { id: 'mtg-025', name: 'German AA - Treffen', type: 'AA', time: '19:30', timezone: 'CET', frequency: 'Mon, Wed, Fri', zoomId: '111 222 333', password: 'deutsch', link: 'https://zoom.us/j/111222333', attendees: '~20', language: 'German' },
  { id: 'mtg-026', name: 'Italian AA - Riunione', type: 'AA', time: '21:00', timezone: 'CET', frequency: 'Daily', zoomId: '222 333 444', password: 'italia', link: 'https://zoom.us/j/222333444', attendees: '~18', language: 'Italian' },
  { id: 'mtg-027', name: 'Russian AA - Встреча', type: 'AA', time: '19:00', timezone: 'MSK', frequency: 'Daily', zoomId: '333 444 555', password: 'russia', link: 'https://zoom.us/j/333444555', attendees: '~30', language: 'Russian' },
  { id: 'mtg-028', name: 'Ukrainian AA - Зустріч', type: 'AA', time: '20:00', timezone: 'EET', frequency: 'Daily', zoomId: '444 555 666', password: 'ukraine', link: 'https://zoom.us/j/444555666', attendees: '~22', language: 'Ukrainian' },
  { id: 'mtg-029', name: 'Canada East AA', type: 'AA', time: '20:00', timezone: 'EST', frequency: 'Daily', zoomId: '555 666 777', password: 'canada', link: 'https://zoom.us/j/555666777', attendees: '~45', language: 'English' },
  { id: 'mtg-030', name: 'Canada West AA', type: 'AA', time: '19:00', timezone: 'PST', frequency: 'Daily', zoomId: '666 777 888', password: 'pacific', link: 'https://zoom.us/j/666777888', attendees: '~35', language: 'English' },
  
  // Specialty Meetings
  { id: 'mtg-031', name: 'Healthcare Workers AA', type: 'AA', time: '07:00', timezone: 'UTC', frequency: 'Sun, Wed', zoomId: '777 888 999', password: 'health', link: 'https://zoom.us/j/777888999', attendees: '~25', language: 'English', specialGroup: 'Healthcare Workers' },
  { id: 'mtg-032', name: 'Veterans Recovery', type: 'AA', time: '18:00', timezone: 'UTC', frequency: 'Mon, Fri', zoomId: '888 999 000', password: 'vets', link: 'https://zoom.us/j/888999000', attendees: '~30', language: 'English', specialGroup: 'Veterans' },
  { id: 'mtg-033', name: 'First Responders NA', type: 'NA', time: '06:30', timezone: 'UTC', frequency: 'Daily', zoomId: '999 000 111', password: 'respond', link: 'https://zoom.us/j/999000111', attendees: '~20', language: 'English', specialGroup: 'First Responders' },
  { id: 'mtg-034', name: 'Parents in Recovery', type: 'AA', time: '21:30', timezone: 'UTC', frequency: 'Tue, Thu', zoomId: '000 111 222', password: 'parents', link: 'https://zoom.us/j/000111222', attendees: '~28', language: 'English', specialGroup: 'Parents' },
  { id: 'mtg-035', name: 'Atheist/Agnostic AA', type: 'AA', time: '17:00', timezone: 'UTC', frequency: 'Daily', zoomId: '111 222 333', password: 'secular', link: 'https://zoom.us/j/111222333', attendees: '~35', language: 'English', specialGroup: 'Secular' },
];

function ZoomMeetingsPage() {
  useTheme4A();
  const navigate = useNavigate();
  const [meetings] = useState(CURATED_MEETINGS);
  const [filteredMeetings, setFilteredMeetings] = useState(CURATED_MEETINGS);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [languageFilter, setLanguageFilter] = useState('all');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [joinedMeetings, setJoinedMeetings] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [reminders, setReminders] = useState([]);

  // Load favorites and reminders from localStorage and backend.  Defined
  // with useCallback so the effect below can declare it as a stable
  // dependency (fixes the exhaustive-deps lint warning).
  const loadUserPreferences = useCallback(async () => {
    const savedFavorites = JSON.parse(localStorage.getItem('meetingFavorites') || '[]');
    const savedReminders = JSON.parse(localStorage.getItem('meetingReminders') || '[]');
    setFavorites(savedFavorites);
    setReminders(savedReminders);

    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings/preferences`);
      if (response.ok) {
        const data = await response.json();
        setFavorites(data.favorites || []);
        setReminders(data.reminders || []);
        localStorage.setItem('meetingFavorites', JSON.stringify(data.favorites || []));
        localStorage.setItem('meetingReminders', JSON.stringify(data.reminders || []));
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  }, []);

  useEffect(() => {
    loadUserPreferences();
  }, [loadUserPreferences]);

  // Filter meetings
  useEffect(() => {
    let filtered = meetings;
    
    if (showFavoritesOnly) {
      filtered = filtered.filter(m => favorites.includes(m.id));
    }
    
    if (typeFilter !== 'all') {
      filtered = filtered.filter(m => m.type === typeFilter);
    }
    
    if (languageFilter !== 'all') {
      filtered = filtered.filter(m => m.language === languageFilter);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(m => 
        m.name.toLowerCase().includes(query) ||
        m.language.toLowerCase().includes(query) ||
        (m.specialGroup && m.specialGroup.toLowerCase().includes(query)) ||
        m.timezone.toLowerCase().includes(query)
      );
    }
    
    setFilteredMeetings(filtered);
  }, [meetings, searchQuery, typeFilter, languageFilter, showFavoritesOnly, favorites]);

  const toggleFavorite = async (meetingId) => {
    const newFavorites = favorites.includes(meetingId)
      ? favorites.filter(id => id !== meetingId)
      : [...favorites, meetingId];
    
    setFavorites(newFavorites);
    localStorage.setItem('meetingFavorites', JSON.stringify(newFavorites));

    try {
      await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings/favorites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ meeting_id: meetingId, action: favorites.includes(meetingId) ? 'remove' : 'add' })
      });
      toast.success(favorites.includes(meetingId) ? 'Removed from favorites' : 'Added to favorites');
    } catch (error) {
      console.error('Error updating favorites:', error);
    }
  };

  const toggleReminder = async (meetingId) => {
    const meeting = meetings.find(m => m.id === meetingId);
    const hasReminder = reminders.includes(meetingId);
    
    const newReminders = hasReminder
      ? reminders.filter(id => id !== meetingId)
      : [...reminders, meetingId];
    
    setReminders(newReminders);
    localStorage.setItem('meetingReminders', JSON.stringify(newReminders));

    try {
      await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings/reminders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
          meeting_id: meetingId, 
          meeting_name: meeting?.name,
          meeting_time: meeting?.time,
          meeting_timezone: meeting?.timezone,
          action: hasReminder ? 'remove' : 'add' 
        })
      });
      toast.success(hasReminder ? 'Reminder removed' : `Reminder set for ${meeting?.name}`);
    } catch (error) {
      console.error('Error updating reminders:', error);
    }
  };

  const handleJoinMeeting = async (meeting) => {
    try {
      // Log the Zoom meeting join
      await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings/zoom-join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
          meeting_id: meeting.id,
          meeting_name: meeting.name,
          meeting_type: meeting.type,
          zoom_link: meeting.link
        })
      });
      
      setJoinedMeetings(prev => [...prev, meeting.id]);
      
      // Open Zoom in new tab
      window.open(meeting.link, '_blank', 'noopener,noreferrer');
      
      // Navigate to verification page
      const verifyParams = new URLSearchParams({
        id: meeting.id,
        name: meeting.name,
        type: meeting.type,
        zoom: meeting.link
      });
      
      toast.success(`Opening ${meeting.name}... Verification tracking started.`);
      navigate(`/meetings/verify?${verifyParams.toString()}`);
      
    } catch (error) {
      console.error('Error logging meeting join:', error);
      window.open(meeting.link, '_blank', 'noopener,noreferrer');
    }
  };

  const refreshMeetings = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      toast.success('Meeting list updated');
    }, 1000);
  };

  const getCurrentTime = () => {
    return new Date().toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const isHappeningNow = (meetingTime) => {
    const now = new Date();
    const [hours, minutes] = meetingTime.split(':').map(Number);
    const meetingDate = new Date();
    meetingDate.setUTCHours(hours, minutes, 0, 0);
    
    const diffMs = Math.abs(now - meetingDate);
    const diffHours = diffMs / (1000 * 60 * 60);
    
    return diffHours < 1;
  };

  // Get unique languages for filter
  const languages = [...new Set(meetings.map(m => m.language))].sort();

  return (
    <div className="min-h-screen bg-zinc-100 pb-20">
      <PageHeader 
        title="Zoom Meetings"
        parentSection={{ label: 'Meeting Log', route: '/meetings' }}
      />

      <main className="pt-24 pb-6 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
            <div className="flex items-start gap-3">
              <Video className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h2 className="font-semibold text-blue-900 text-sm">Online AA & NA Meetings</h2>
                <p className="text-xs text-blue-700 mt-1">
                  Join Recovery meetings from anywhere. Click "Join" to open Zoom directly. 
                  Use <Star className="w-3 h-3 inline" /> to bookmark favorites and <Bell className="w-3 h-3 inline" /> to set reminders.
                </p>
                <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Current time: {getCurrentTime()} (your local time)
                </p>
              </div>
            </div>
          </div>

          {/* Search & Filters */}
          <div className="bg-white border border-zinc-200 rounded-md p-4 mb-4">
            <div className="flex gap-2 mb-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  placeholder="Search meetings, language, timezone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="meeting-search"
                />
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={refreshMeetings}
                disabled={isLoading}
                data-testid="refresh-meetings"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            
            <div className="flex gap-2 flex-wrap">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[120px]" data-testid="type-filter">
                  <Filter className="w-4 h-4 mr-1" />
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="AA">AA Only</SelectItem>
                  <SelectItem value="NA">NA Only</SelectItem>
                  <SelectItem value="SMART">SMART</SelectItem>
                </SelectContent>
              </Select>

              <Select value={languageFilter} onValueChange={setLanguageFilter}>
                <SelectTrigger className="w-[130px]" data-testid="language-filter">
                  <Globe className="w-4 h-4 mr-1" />
                  <SelectValue placeholder="Language" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Languages</SelectItem>
                  {languages.map(lang => (
                    <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                variant={showFavoritesOnly ? "default" : "outline"}
                size="sm"
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                className={showFavoritesOnly ? "bg-amber-500 hover:bg-amber-600" : ""}
                data-testid="favorites-filter"
              >
                <Star className={`w-4 h-4 mr-1 ${showFavoritesOnly ? 'fill-white' : ''}`} />
                Favorites ({favorites.length})
              </Button>
            </div>
          </div>

          {/* Meeting List */}
          <div className="space-y-3 mb-8">
            <p className="text-xs font-bold text-zinc-600 uppercase tracking-wider flex items-center justify-between">
              <span>Available Meetings ({filteredMeetings.length})</span>
            </p>
            
            {filteredMeetings.length === 0 ? (
              <div className="bg-white border border-zinc-200 rounded-md p-8 text-center">
                <p className="text-zinc-500">No meetings match your filters</p>
              </div>
            ) : (
              filteredMeetings.map((meeting) => {
                const isNow = isHappeningNow(meeting.time);
                const hasJoined = joinedMeetings.includes(meeting.id);
                const isFavorite = favorites.includes(meeting.id);
                const hasReminder = reminders.includes(meeting.id);
                
                return (
                  <div 
                    key={meeting.id} 
                    className={`bg-white border rounded-md p-4 transition-all ${
                      isNow ? 'border-green-400 shadow-sm' : 
                      isFavorite ? 'border-amber-300' : 'border-zinc-200'
                    }`}
                    data-testid={`meeting-card-${meeting.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                            meeting.type === 'AA' ? 'bg-blue-100 text-blue-700' :
                            meeting.type === 'NA' ? 'bg-purple-100 text-purple-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {meeting.type}
                          </span>
                          {isNow && (
                            <span className="text-xs font-bold px-2 py-0.5 rounded bg-green-500 text-white animate-pulse">
                              LIVE NOW
                            </span>
                          )}
                          {meeting.specialGroup && (
                            <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700">
                              {meeting.specialGroup}
                            </span>
                          )}
                        </div>
                        <h3 className="font-semibold text-zinc-900">{meeting.name}</h3>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => toggleFavorite(meeting.id)}
                          className={`p-2 rounded-full transition-colors ${
                            isFavorite ? 'text-amber-500 bg-amber-50' : 'text-zinc-400 hover:bg-zinc-100'
                          }`}
                          data-testid={`favorite-${meeting.id}`}
                        >
                          <Star className={`w-4 h-4 ${isFavorite ? 'fill-amber-500' : ''}`} />
                        </button>
                        <button
                          onClick={() => toggleReminder(meeting.id)}
                          className={`p-2 rounded-full transition-colors ${
                            hasReminder ? 'text-blue-500 bg-blue-50' : 'text-zinc-400 hover:bg-zinc-100'
                          }`}
                          data-testid={`reminder-${meeting.id}`}
                        >
                          {hasReminder ? <Bell className="w-4 h-4 fill-blue-500" /> : <BellOff className="w-4 h-4" />}
                        </button>
                        <Button
                          onClick={() => handleJoinMeeting(meeting)}
                          className={`ml-2 ${hasJoined ? 'bg-green-600 hover:bg-green-700' : 'bg-zinc-950 hover:bg-zinc-800'}`}
                          size="sm"
                          data-testid={`join-meeting-${meeting.id}`}
                        >
                          <Video className="w-4 h-4 mr-1" />
                          {hasJoined ? 'Rejoin' : 'Join'}
                        </Button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-xs text-zinc-600">
                      <div className="flex items-center gap-1 col-span-2">
                        <Clock className="w-3 h-3" />
                        <span>
                          {(() => {
                            const { localTime, tzAbbr } = convertToLocalTime(meeting.time, meeting.timezone);
                            return (
                              <span className="font-medium text-zinc-900">
                                {localTime} {tzAbbr}
                              </span>
                            );
                          })()}
                          <span className="text-zinc-400 ml-1">• {meeting.frequency}</span>
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Globe className="w-3 h-3" />
                        <span>{meeting.language}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        <span>{meeting.attendees} typical</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Video className="w-3 h-3" />
                        <span>ID: {meeting.zoomId}</span>
                      </div>
                    </div>
                    
                    {meeting.password && (
                      <div className="mt-2 text-xs bg-zinc-50 px-2 py-1 rounded inline-block">
                        <span className="text-zinc-500">Password:</span>{' '}
                        <span className="font-mono text-zinc-700">{meeting.password}</span>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Find Physical Meetings */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-md p-4 mb-4 text-white">
            <div className="flex items-start gap-3">
              <div className="bg-white/20 p-2 rounded-lg">
                <MapPin className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-sm mb-1">Find In-Person Meetings Near You</h3>
                <p className="text-xs text-emerald-100 mb-3">
                  Search for physical AA, NA and other 12-step meetings in your local area
                </p>
                <div className="flex flex-wrap gap-2">
                  <a
                    href="https://www.aa.org/find-aa"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
                    data-testid="find-aa-physical"
                  >
                    <span>AA Meetings</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <a
                    href="https://www.na.org/meetingsearch/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
                    data-testid="find-na-physical"
                  >
                    <span>NA Meetings</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <a
                    href="https://adultchildren.org/meeting-search/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
                    data-testid="find-aca-physical"
                  >
                    <span>ACA Meetings</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <a
                    href="https://www.smartrecovery.org/local/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
                    data-testid="find-smart-physical"
                  >
                    <span>SMART Recovery</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* External Resources */}
          <div className="bg-white border border-zinc-200 rounded-md p-4">
            <p className="text-xs font-bold text-zinc-600 uppercase tracking-wider mb-4">
              More Meeting Resources
            </p>
            
            <div className="space-y-3">
              {MEETING_SOURCES.map((source) => (
                <a
                  key={source.id}
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 rounded-md border border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50 transition-all"
                  data-testid={`source-${source.id}`}
                >
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm text-zinc-900">{source.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        source.type === 'AA' ? 'bg-blue-100 text-blue-700' :
                        source.type === 'NA' ? 'bg-purple-100 text-purple-700' :
                        source.type === 'SMART' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {source.type}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-600">{source.description}</p>
                    <p className="text-xs text-zinc-500 mt-1">{source.meetingCount} meetings • {source.languages.join(', ')}</p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-zinc-400" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

export default ZoomMeetingsPage;
